/**
 * Tone.js audio engine — wrapper lazy + singleton-friendly pour la couche polish.
 *
 * Caractéristiques :
 *   - Lazy init : `import('tone')` n'est exécuté qu'au 1er appel à `init()`
 *     (ne charge pas les ~120KB de Tone.js avant le 1er user gesture).
 *   - Master gain node : volume global modifiable (-60..0 dBFS).
 *   - Browser policy : Tone.start() doit être appelé après un user gesture.
 *     Le 1er trial response (clic) est un user gesture valide → init OK depuis polish.
 *   - dispose() : cleanup AudioContext + master gain. Idempotent.
 *
 * Module-loader compatible : Tone v15 est ESM-pure. L'`import dynamique` permet
 * (a) de différer le coût bundle et (b) de mocker via `vi.mock('tone', ...)` côté tests.
 */
/**
 * Les engines sont plus lourds que des objets stateless ; on factorise une
 * interface `ToneEngine` pour pouvoir injecter un fake en test (vi.mocked).
 */
export function createToneEngine() {
    let toneNs = null;
    let master = null;
    let initPromise = null;
    let masterDb = -6;
    async function init() {
        if (toneNs && master)
            return;
        if (initPromise)
            return initPromise;
        initPromise = (async () => {
            const mod = await import('tone');
            toneNs = mod;
            // Tone.start() unlock le contexte AudioContext (browser autoplay policy).
            // Sur node/jsdom (tests), le mock court-circuite cette branche.
            try {
                await mod.start();
            }
            catch {
                /* ignored — environnements sans AudioContext */
            }
            master = new mod.Gain(dbToGain(masterDb)).toDestination();
        })();
        try {
            await initPromise;
        }
        finally {
            // En cas d'échec on autorise un retry au prochain appel.
            if (!toneNs || !master)
                initPromise = null;
        }
    }
    function isInitialized() {
        return toneNs !== null && master !== null;
    }
    function setMasterVolume(db) {
        masterDb = clamp(db, -60, 0);
        if (master)
            master.gain.value = dbToGain(masterDb);
    }
    function getTone() {
        if (!toneNs)
            throw new Error('[tone-engine] not initialized — call init() first');
        return toneNs;
    }
    function getMasterOutput() {
        if (!master)
            throw new Error('[tone-engine] not initialized — call init() first');
        return master;
    }
    function dispose() {
        try {
            master?.dispose();
        }
        catch {
            /* ignored */
        }
        master = null;
        toneNs = null;
        initPromise = null;
    }
    return {
        init,
        isInitialized,
        setMasterVolume,
        getTone,
        getMasterOutput,
        dispose,
    };
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function dbToGain(db) {
    return Math.pow(10, db / 20);
}
//# sourceMappingURL=tone-engine.js.map